import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookingTitlesCard } from "@/components/crm/booking-titles-card";
import {
	CrmLoadError,
	CrmPageHeader,
	CrmRoleNotice,
} from "@/components/crm/crm-page";
import { GoogleConnectionCard } from "@/components/crm/google-connection-card";
import { OwnerSelect } from "@/components/crm/owner-select";
import { SignatureCard } from "@/components/crm/signature-card";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { decodeCrmError } from "@/lib/crm/format";
import { CRM_PICKER_STALE_TIME, invalidateCrmData } from "@/lib/crm/queries";
import { orpc } from "@/utils/orpc";

type SettingsSearch = { google?: "connected" | "error" };

export const Route = createFileRoute("/crm/settings")({
	component: CrmSettingsPage,
	validateSearch: (search: Record<string, unknown>): SettingsSearch =>
		search.google === "connected" || search.google === "error"
			? { google: search.google }
			: {},
});

/**
 * CRM settings: every manager's own Google connection; for crm_admin also the
 * default owner of new records (decision 25).
 */
function CrmSettingsPage(): ReactElement {
	const { t } = useTranslation();
	const { canManage, canAdmin } = useCrmPermissions();
	const { google } = Route.useSearch();

	if (!canManage) return <CrmRoleNotice />;

	return (
		<div className="space-y-4">
			<CrmPageHeader title={t("admin:crm.settings.title")} />
			<GoogleConnectionCard result={google} />
			<SignatureCard />
			{canAdmin && <DefaultOwnerCard />}
			{canAdmin && <BookingTitlesCard />}
		</div>
	);
}

/** The default owner of new records (crm_admin). */
function DefaultOwnerCard(): ReactElement {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { data, isLoading, isError } = useQuery({
		...orpc.admin.crm.settings.get.queryOptions(),
		staleTime: CRM_PICKER_STALE_TIME,
	});
	const update = useMutation(orpc.admin.crm.settings.update.mutationOptions());

	const setDefaultOwner = async (userId: string | null): Promise<void> => {
		try {
			await update.mutateAsync({ defaultOwnerUserId: userId });
			await invalidateCrmData(queryClient);
			toast.success(t("admin:crm.settings.saved"));
		} catch (error) {
			toast.error(
				decodeCrmError(error).message ?? t("admin:crm.errors.generic"),
			);
		}
	};

	return (
		<Card className="max-w-xl">
			<CardHeader>
				<CardTitle className="text-base">
					{t("admin:crm.settings.default_owner")}
				</CardTitle>
				<CardDescription>
					{t("admin:crm.settings.default_owner_description")}
				</CardDescription>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<Skeleton className="h-9" />
				) : isError || !data ? (
					<CrmLoadError />
				) : (
					<div className="space-y-1.5">
						<Label htmlFor="crm-default-owner">
							{t("admin:crm.common.owner")}
						</Label>
						<OwnerSelect
							id="crm-default-owner"
							value={data.defaultOwnerUserId}
							disabled={update.isPending}
							onChange={(value) => void setDefaultOwner(value ?? null)}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
